<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import ResearchCitationPanels from '@/components/ResearchCitationPanels.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import SharedContentBanner from '@/components/SharedContentBanner.vue'
import TermsOfUseFooter from '@/components/TermsOfUseFooter.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import StrategyBanner from './details/StrategyBanner.vue'
import StrategyPanels from './details/StrategyPanels.vue'
import StrategyRelated from './details/StrategyRelated.vue'
import StrategyResourceList from './details/StrategyResourceList.vue'
import { getSharedStrategy } from './api'
import { strategyPageSections } from './strategy_page_sections'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import type { SharedStrategy } from './strategy_types'
import { LIGHT_STRATEGY_TEXT } from './strategy_text_classes'

const props = defineProps<{ uuid: string }>()

const router = useRouter()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const sharedStrategy = ref<SharedStrategy | null>(null)
const linkInvalid = ref(false)

const sourceStrategy = computed(() => sharedStrategy.value?.source_strategy)

const analytics = getAnalyticsTracker().getChildContext({
  strategy_uuid: computed(() => sourceStrategy.value?.uuid),
  strategy_title: computed(() => sourceStrategy.value?.title),
  student_uuid: computed(() => sharedStrategy.value?.student_uuid),
  uuid: computed(() => props.uuid),
  title: computed(() => sourceStrategy.value?.title)
})

/** A shared copy's tips and examples are its owner's to show, so they are never blurred. */
const sections = computed(
  () => sharedStrategy.value && strategyPageSections({ ...sharedStrategy.value, ...sharedStrategy.value.source_strategy }, true)
)

const shareLink = computed(
  () => window.location.origin + router.resolve({ name: 'SharedStrategyDetailPage', params: { uuid: props.uuid } }).href
)

function showSharedStrategy(loaded: SharedStrategy) {
  const { title } = loaded.source_strategy
  sharedStrategy.value = loaded
  setTitle(`${title} | UDL Strategies`)
  updateFeedbackContext({ uuid: loaded.uuid, title })
  analytics.sendEvent(
    EVENT_NAMES.STRATEGY_DETAIL.VIEWED_SHARED_PAGE,
    { uuid: loaded.uuid, title },
    { cu: loaded.uuid, ct: title }
  )
}

let latestFetch = 0

async function fetchContent() {
  const thisFetch = ++latestFetch
  linkInvalid.value = false
  sharedStrategy.value = null
  try {
    const loaded = await getSharedStrategy(props.uuid)
    if (thisFetch !== latestFetch) return
    showSharedStrategy(loaded)
  } catch (error) {
    if (thisFetch !== latestFetch) return
    console.error(error)
    linkInvalid.value = true
  }
}

watch(() => props.uuid, fetchContent, { immediate: true })
</script>

<template>
  <div>
    <ShareLinkInvalidMessage v-if="linkInvalid" class="mt-6">
      This link is either invalid, or the strategy is no longer shared by its owner. Ask the owner of the strategy for a new shareable link.
    </ShareLinkInvalidMessage>
    <template v-else-if="sharedStrategy && sourceStrategy && sections">
      <SharedContentBanner
        content-type="a strategy"
        :owner-name="sharedStrategy.user_info.name"
        :last-updated="sharedStrategy.updated_at"
        :share-link="shareLink"
        :share-content-type="CONTENT_TYPES.SAVED_STRATEGY"
        :container-class="VUE2_CONTAINER_WIDTHS"
        auto-hide-copy-tooltip
      />
      <div class="container pt-5" :class="VUE2_CONTAINER_WIDTHS">
        <CopyTrackWrapper
          :type="CONTENT_TYPES.SHARED_STRATEGY"
          :title="sourceStrategy.title"
          :uuid="sharedStrategy.uuid"
          gate-copy
          :demo="sourceStrategy.demo"
        >
          <CopyTrackWrapper type="strategy_banner" :title="sourceStrategy.title" :uuid="sharedStrategy.uuid" gate-copy>
            <StrategyBanner v-bind="sections.banner" />
          </CopyTrackWrapper>
          <div class="md:grid md:grid-cols-12 md:gap-x-[30px]">
            <div class="md:col-span-8">
              <CopyTrackWrapper
                v-if="sharedStrategy.notes"
                type="strategy_notes"
                gate-copy
                :title="sourceStrategy.title"
                :uuid="sharedStrategy.uuid"
                class="fs-exclude bg-yellow-100 px-[15px] py-2.5 text-gray-900 shadow-lib-card"
                data-testid="owner-notes"
              >
                <h2 class="mb-2.5 flex items-center gap-1 text-display-sm font-bold leading-[1.1] text-blue-400">
                  <span class="material-icons-round" aria-hidden="true">push_pin</span>
                  {{ sharedStrategy.user_info.name }}'s Notes
                </h2>
                <WikiText :text="sharedStrategy.notes" :class="['text-text-2xl leading-[33px]', LIGHT_STRATEGY_TEXT]" />
              </CopyTrackWrapper>
              <StrategyResourceList v-bind="sections.resources" />
              <StrategyPanels v-bind="sections.implementation" />
              <StrategyPanels v-bind="sections.examples" />
              <ResearchCitationPanels v-if="sections.researchCitations" :research-citations="sections.researchCitations" />
            </div>
          </div>
          <StrategyRelated v-if="sections.related" v-bind="sections.related" />
          <TermsOfUseFooter />
        </CopyTrackWrapper>
      </div>
    </template>
    <div v-else class="container pt-5" :class="VUE2_CONTAINER_WIDTHS">
      <TermsOfUseFooter />
    </div>
  </div>
</template>
